<template>
	<node-view-wrapper class="teaming-tiptap-commitment rte-node--draggable">
		<div
			class="rte-node__drag-handle"
			contenteditable="false"
			draggable="true"
			data-drag-handle
		/>
		<div class="teaming-tiptap-commitment__row">
			<div class="teaming-tiptap-commitment__checkbox">
				<TeamingCheckbox
					name="isCommitmentComplete"
					:isChecked="isChecked"
					value="yes"
					@change="onCompletionChange"
				/>
			</div>
			<div class="teaming-tiptap-commitment__content">
				<node-view-content class="teaming-tiptap-commitment__editable-content rte-node__content--draggable" />
			</div>
			<div class="teaming-tiptap-commitment__date-picker">
				<div
					ref="datePickerActivator"
					id="commitment-date-picker"
				>
					<CommitmentDueDatePicker
						:due="dateValue"
						:completed="completedDate"
						@click="onToggleCalendar"
					/>
				</div>
			</div>
			<div>
				<CommitmentAssigneePicker
					:members="teamMembers"
					:selected-member-id="assigneeId"
					:is-open="isAssigneeMenuOpen"
					@toggle="onAssigneePickerToggled"
					@select="onAssigneeSelected"
				/>
			</div>
		</div>
		<OverlayPanel ref="datePicker">
			<CommitmentCalendar
				:value="defaultDatePickerValue"
				@setDate="onSetDate"
				@cancel="onCancel"
				@removeDate="onRemoveDate"
			/>
		</OverlayPanel>
	</node-view-wrapper>
</template>

<script>
import moment from "moment-timezone";
import OverlayPanel from "primevue/overlaypanel";
import { NodeViewWrapper, NodeViewContent, nodeViewProps } from "@tiptap/vue-3";
import CommitmentAssigneePicker from "@teaming/common-ui/components/commitments/CommitmentAssigneePicker";
import CommitmentCalendar from "@teaming/common-ui/components/commitments/CommitmentCalendar";
import CommitmentDueDatePicker from "@teaming/common-ui/components/commitments/CommitmentDueDatePicker";
import TeamingCheckbox from "@teaming/common-ui/components/TeamingCheckbox";
import { TeamingTiptapExtensionType } from "@teaming/common-enums";

export default {
	name: "TeamingTiptapCommitment",
	components: {
		CommitmentAssigneePicker,
		CommitmentCalendar,
		CommitmentDueDatePicker,
		OverlayPanel,
		NodeViewWrapper,
		NodeViewContent,
		TeamingCheckbox,
	},

	props: nodeViewProps,

	inject: [
		"context",
	],

	data() {
		return {
			isAssigneeMenuOpen: false,
			isCalendarOpen: false,
			globalClickHandler: null,
		};
	},

	computed: {
		dateValue() {
			// due date is stored as UTC in the DB, so we need the
			// tiptap/prosemirror attributes to be consistent with that
			return this.node.attrs[ "due-date" ] ?
				moment.utc( this.node.attrs[ "due-date" ] ).toDate() :
				null;
		},
		defaultDatePickerValue() {
			return this.dateValue || new Date();
		},
		isChecked() {
			return !!this.node.attrs[ "completed-date" ];
		},
		completedDate() {
			// due date is stored as UTC in the DB, so we need the
			// tiptap/prosemirror attributes to be consistent with that
			return this.node.attrs[ "completed-date" ] ?
				moment.utc( this.node.attrs[ "completed-date" ] ).toDate() :
				null;
		},
		assigneeId() {
			return this.node.attrs[ "assigned-to" ] || "";
		},
		teamMembers() {
			return this.context.teamMembers || [];
		},
	},

	watch: {
		isAssigneeMenuOpen( newVal, oldVal ) {
			if ( !newVal && oldVal ) {
				this.editor.commands.focus();
			}
		},
		isCalendarOpen( newVal, oldVal ) {
			if ( !newVal && oldVal ) {
				this.editor.commands.focus();
			}
		},
	},

	methods: {
		onCompletionChange( e ) {
			const { isChecked, } = e;
			this.updateAttributes( {
				"completed-date": isChecked ? moment.utc().format( "YYYY-MM-DD HH:mm:ss" ) : null,
			} );
		},

		onToggleCalendar( event ) {
			this.editor.commands.blur();
			this.$refs.datePicker.toggle( event, this.$refs.datePickerActivator );
			this.isCalendarOpen = true;
		},

		onSetDate( date ) {
			const dueDate = moment( date ).format();
			this.$refs.datePicker.hide();
			this.isCalendarOpen = false;
			this.updateAttributes( { "due-date": dueDate, } );
		},

		onAssigneePickerToggled() {
			const isOpen = this.isAssigneeMenuOpen;
			// force other instances of TeamingTiptapCommitment to close
			// their assignee pickers before this one is toggled
			window.dispatchEvent( new Event( "click" ) );
			this.$nextTick( () => {
				const isMenuOpen = !isOpen;
				if ( isMenuOpen ) {
					this.editor.commands.blur();
				}
				this.isAssigneeMenuOpen = isMenuOpen;
			} );
		},

		onAssigneeSelected( member ) {
			this.isAssigneeMenuOpen = false;
			const personId = member ? member.personId : null;
			this.updateAttributes( { "assigned-to": personId, } );
			this.updateAttributes( { "assigned-by": this.context.personId, } );
		},

		onCancel() {
			this.$refs.datePicker.hide();
			this.isCalendarOpen = false;
		},

		onRemoveDate() {
			const dueDate = null;
			this.$refs.datePicker.hide();
			this.isCalendarOpen = false;
			this.updateAttributes( { "due-date": dueDate, } );
		},
	},

	mounted() {
		this.globalClickHandler = () => {
			this.isAssigneeMenuOpen = false;
		};
		window.addEventListener( "click", this.globalClickHandler );
	},

	beforeUnmount() {
		if ( this.globalClickHandler ) {
			window.removeEventListener( "click", this.globalClickHandler );
			this.globalClickHandler = null;
		}
	},

	unmounted() {
		this.extension?.options?.onTeamingExtensionCallback( { nodeType: TeamingTiptapExtensionType.COMMITMENT, type: "destroy", } );
	},

};
</script>

<style lang="scss">
.teaming-tiptap-commitment {
	.rte-node__drag-handle {
		transform: translateY(10%);
	}
}
</style>

<style lang="scss" scoped>
.teaming-tiptap-commitment {
	@include font-and-line-for(0);
	color: $tiptap-commitment-font-color;
	width: 100%;
}

.teaming-tiptap-commitment__row {
	display: flex;
	align-content: center;
	width: 100%;
}

.teaming-tiptap-commitment__checkbox {
	margin-right: 0.625rem;
}

.teaming-tiptap-commitment__content {
	flex-grow: 1;
}

.teaming-tiptap-commitment__date-picker {
	margin-right: .5rem;
}
</style>
